import { inject, observer } from 'mobx-react'
import { withRouter } from 'react-router-dom'
import * as React from 'react'
import { History } from 'history'
import { InnerContainer } from '../../shared/components/Elements'
import { breakpoint, styled } from '../../shared/styled-components'
import { CalculatorStoreInstance, IStepInfo } from '../../stores/CalculatorStore'
import { Utils } from '../../shared/Utils'

interface IProps {
  store?: CalculatorStoreInstance
  history?: History
}

@(withRouter as any)
@inject('store')
@observer
export default class Progress extends React.Component<IProps> {
  private _onStepClick (step: IStepInfo) {
    if (step.path && step.isDone()) {
      this.props.history!.push(step.path)
    }
  }

  private _getProgress () {
    const { steps } = this.props.store

    let step: number = 0
    for (let stepName in steps) {
      const { pathMatch } = steps[stepName]
      const pathMatches = location.pathname.match(pathMatch)
      if (step === 0 && pathMatches) break

      if (!pathMatches) {
        step++
      } else {
        break
      }
    }
    return step / (Object.values(steps).length - 1)
  }

  private _renderSteps () {
    const { steps } = this.props.store

    const progress = this._getProgress()
    const stepElements = []
    let i = 0
    for (let stepName in steps) {
      const step = steps[stepName]
      const stepProgress = (i / (Object.values(steps).length - 1))
      const stepActive = progress === stepProgress
      const stepDone = progress > stepProgress
      stepElements.push(<Step
        key={stepName}
        active={stepActive}
        done={stepDone}
        clickable={step.isDone && step.isDone()}
        onClick={this._onStepClick.bind(this, step)}>
        <StepLabel>{step.title}</StepLabel>
      </Step>)
      i++
    }
    return stepElements
  }

  render (): React.ReactNode {
    return (<Container>
      <InnerContainer>
        <InnerSpacer>
          <ProgressLine progress={this._getProgress()} />
          {this._renderSteps()}
        </InnerSpacer>
      </InnerContainer>
    </Container>)
  }
}

const Container = styled.div`
  background: white;
  height: 150px;
  flex-direction: row;
  padding-top: 30px;
  display: none;
  ${breakpoint('tablet')`
    display: flex;
  `}
`

const InnerSpacer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 0 60px;
`

const Step = styled.div<{ active?: boolean, done?: boolean, clickable?: boolean }>`
  position: relative;
  width: 39px;
  height: 39px;
  border-radius: 50%;
  background: white;
  font-weight: 500;
  cursor: ${props => props.clickable ? 'pointer' : 'not-allowed'};
  ${props => !props.active && !props.done && `border: 4px solid ${props.theme.borderColor};`}
  ${props => props.active && `
    box-shadow: 2px 2px 11px 0 rgba(164,164,164,0.50);
    &:after {
      content: '';
      display: block;
      width: 20px;
      height: 20px;
      background: ${props.theme.primaryColor};
      border-radius: 50%;
      position: absolute;
      top: 50%;
      left: 50%;
      margin-top: -10px;
      margin-left: -10px;
    }
  `}
  ${props => props.done && `
    background: ${props.theme.primaryColor} url(${Utils.getAssetPath('/assets/icons/check.png')}) no-repeat center center;
    background-size: 20px;
  `}
`

const StepLabel = styled.div`
  font-size: 14px;
  text-transform: uppercase;
  position: absolute;
  top: 75px;
  width: 150px;
  transform: translateX(-50%);
  left: 50%;
  text-align: center;
  color: ${props => props.theme.headlineColor};
`

const ProgressLine = styled.div<{ progress: number }>`
  position: absolute;
  top: 50%;
  margin-top: -2px;
  height: 4px;
  width: 100%;
  background: ${props => props.theme.borderColor};
  &:after {
    content: '';
    display: block;
    height: 4px;
    width: ${props => props.progress * 100}%;
    background: ${props => props.theme.primaryColor};
  }
`
